import { Box, Button, CircularProgress, Typography } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';

import ErrorState from 'app/shared-components/common/ErrorState';

import { useGetWebsiteTheme, type WebsiteThemeFavicon } from './hooks/useGetWebsiteTheme';
import { getPrimaryFavicon } from './favicons';

function EditButton({ onClick }: { onClick?: () => void }): ReactElement {
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      className="vtwo:rounded-lg vtwo:px-6 vtwo:py-1 vtwo:text-featured vtwo:border-featured vtwo:hover:border-featured-hover vtwo:hover:bg-featured/[0.04]"
    >
      Edit
    </Button>
  );
}

function SectionCard({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <Box
      className={`vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white vtwo:p-6 ${className}`}
    >
      {children}
    </Box>
  );
}

function CardHeader({ title, onEdit }: { title: string; onEdit?: () => void }): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:mb-4">
      <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary">
        {title}
      </Typography>
      <EditButton onClick={onEdit} />
    </Box>
  );
}

function BrandPaletteCard({
  primaryColor,
  onEdit,
}: {
  primaryColor: string;
  onEdit: () => void;
}): ReactElement {
  return (
    <SectionCard>
      <CardHeader title="Brand palette" onEdit={onEdit} />
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-3">
        <Box
          className="vtwo:size-5 vtwo:rounded-md vtwo:border vtwo:border-black/10 vtwo:shrink-0"
          style={{ backgroundColor: primaryColor }}
        />
        <Typography className="vtwo:text-typography-primary">
          Primary color - {primaryColor}
        </Typography>
      </Box>
    </SectionCard>
  );
}

interface LogoCardProps {
  logoUrl: string;
  onEdit: () => void;
}

function LogoCard({ logoUrl, onEdit }: LogoCardProps): ReactElement {
  return (
    <SectionCard className="vtwo:h-full vtwo:flex vtwo:flex-col">
      <CardHeader title="Logo" onEdit={onEdit} />
      <Box className="vtwo:flex-[1] vtwo:flex vtwo:items-center vtwo:justify-center vtwo:rounded-lg vtwo:border vtwo:border-plus-border vtwo:bg-plus-canvas vtwo:py-8">
        <img src={logoUrl} alt="Website logo" className="vtwo:h-10 vtwo:object-contain" />
      </Box>
    </SectionCard>
  );
}

function FaviconCard({
  favicon,
  onEdit,
}: {
  favicon?: WebsiteThemeFavicon;
  onEdit: () => void;
}): ReactElement {
  return (
    <SectionCard>
      <Box className="vtwo:flex vtwo:items-center vtwo:justify-between">
        <Box className="vtwo:flex vtwo:items-center vtwo:gap-3">
          {favicon && (
            <img src={favicon.url} alt="Favicon" className="vtwo:size-6 vtwo:object-contain" />
          )}
          <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary">
            Favicon
          </Typography>
        </Box>
        <EditButton onClick={onEdit} />
      </Box>
    </SectionCard>
  );
}

const ThemeAndStylesTab = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { websiteId } = useOutletContext<{ websiteId: string }>();
  const organisationId = searchParams.get('organisationId') ?? '';

  const { theme, isLoading, isError, error } = useGetWebsiteTheme({ websiteId });

  const handleEditLogo = () => {
    navigate(
      {
        pathname: '/plus-websites/theme-and-styles/logo',
        search: new URLSearchParams({ organisationId, websiteId }).toString(),
      },
      { state: { logoUrl: theme?.logoUrl } }
    );
  };

  const handleEditBrandPalette = () => {
    navigate({
      pathname: '/plus-websites/theme-and-styles/brand-palette',
      search: new URLSearchParams({ organisationId, websiteId }).toString(),
    });
  };

  const handleEditFavicon = () => {
    navigate({
      pathname: '/plus-websites/theme-and-styles/favicon',
      search: new URLSearchParams({ organisationId, websiteId }).toString(),
    });
  };

  if (isError) {
    return (
      <Box className="vtwo:px-16 vtwo:pt-8 vtwo:pb-16">
        <ErrorState
          title="Failed to load theme"
          subtitle={error instanceof Error ? error.message : 'Try again later'}
        />
      </Box>
    );
  }

  if (isLoading || !theme) {
    return (
      <Box className="vtwo:px-16 vtwo:py-24 vtwo:flex vtwo:justify-center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box className="vtwo:px-16 vtwo:pt-8 vtwo:pb-16">
      <Box className="vtwo:flex vtwo:flex-col vtwo:md:flex-row vtwo:gap-6">
        <Box className="vtwo:flex-1 vtwo:flex vtwo:flex-col vtwo:gap-2">
          <BrandPaletteCard
            primaryColor={theme.colorPalette.primary}
            onEdit={handleEditBrandPalette}
          />
          <FaviconCard favicon={getPrimaryFavicon(theme.favicons)} onEdit={handleEditFavicon} />
        </Box>
        <Box className="vtwo:flex-1 vtwo:flex vtwo:flex-col vtwo:gap-2">
          <LogoCard logoUrl={theme.logoUrl} onEdit={handleEditLogo} />
        </Box>
      </Box>
    </Box>
  );
};

export default ThemeAndStylesTab;
